import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { HashRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/common/Navbar';
import FeatureFlashSlider from './components/common/FeatureFlashSlider';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';
import FarmLifecycleLoader from './components/common/FarmLifecycleLoader';
import { useAuthStore } from './store/authStore';
import HomePage from './pages/HomePage';

const FarmerPage = lazy(() => import('./pages/FarmerPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ServiceUnavailablePage = lazy(() => import('./pages/ServiceUnavailablePage'));
const RiceStorePage = lazy(() => import('./pages/RiceStorePage'));
const GrainStorePage = lazy(() => import('./pages/GrainStorePage'));
const RituRootsPage = lazy(() => import('./pages/RituRootsPage'));
const EquipmentStorePage = lazy(() => import('./pages/EquipmentStorePage'));
const EquipmentDetailPage = lazy(() => import('./pages/EquipmentDetailPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const JoinUsPage = lazy(() => import('./pages/JoinUsPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));
const AuthPage = lazy(() => import('./pages/auth/AuthPage'));
const OrgLoginPage = lazy(() => import('./pages/auth/OrgLoginPage'));
const SetupSuperAdminPage = lazy(() => import('./pages/auth/SetupSuperAdminPage'));
const ManagerDashboard = lazy(() => import('./pages/dashboards/ManagerDashboard'));
const AddStaffPage = lazy(() => import('./pages/dashboards/AddStaffPage'));
const CEODashboard = lazy(() => import('./pages/dashboards/CEODashboard'));
const StaffPortfolioPage = lazy(() => import('./pages/dashboards/StaffPortfolioPage'));
const SupervisorDashboard = lazy(() => import('./pages/dashboards/SupervisorDashboard'));
const AddFarmerPage = lazy(() => import('./pages/dashboards/AddFarmerPage'));
const AddDealerPage = lazy(() => import('./pages/dashboards/AddDealerPage'));
const AddEquipmentDealPage = lazy(() => import('./pages/dashboards/AddEquipmentDealPage'));
const FarmerDashboard = lazy(() => import('./pages/dashboards/FarmerDashboard'));
const DealerDashboard = lazy(() => import('./pages/dashboards/DealerDashboard'));
const DeliveryDashboard = lazy(() => import('./pages/dashboards/DeliveryDashboard'));
const ManageProductsPage = lazy(() => import('./pages/dashboards/ManageProductsPage'));
const ManageDealsPage = lazy(() => import('./pages/dashboards/ManageDealsPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/legal/TermsAndConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const ShippingPolicyPage = lazy(() => import('./pages/legal/ShippingPolicyPage'));
const ContactUsPage = lazy(() => import('./pages/legal/ContactUsPage'));
const CancellationRefundsPage = lazy(() => import('./pages/legal/CancellationRefundsPage'));

const routeFallback = (
  <FarmLifecycleLoader
    compact
    title="Loading page"
    subtitle="Preparing fresh content for you..."
  />
);

function AppShell() {
  const { bootstrapSession } = useAuthStore();
  const location = useLocation();
  const hideMarketingChrome = location.pathname === '/checkout';
  const showFeatureFlash = location.pathname === '/';
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    bootstrapSession().catch(() => {
      // Session cleanup is handled in saga.
    });
  }, [bootstrapSession]);

  return (
    <div className="min-h-screen bg-cream font-sans">
      <Navbar />
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/farmer/:id" element={<FarmerPage />} />
          <Route
            path="/cart"
            element={
              <ProtectedRoute allowedRoles={['customer', 'farmer']}>
                <CartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute allowedRoles={['customer', 'farmer']}>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route path="/service-unavailable" element={<ServiceUnavailablePage />} />
          <Route path="/rice-store" element={<RiceStorePage />} />
          <Route path="/grain-store" element={<GrainStorePage />} />
          <Route path="/ritu-roots" element={<RituRootsPage />} />
          <Route path="/equipment-store" element={<EquipmentStorePage />} />
          <Route path="/equipment/:id" element={<EquipmentDetailPage />} />
          <Route
            path="/products"
            element={
              <RiceStorePage
                initialCategory="All"
                storeScope="all"
                storeTitle="All Products"
                storeSubtitle="Browse every available product from verified farmers in one place."
              />
            }
          />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/join-us" element={<JoinUsPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/org" element={<OrgLoginPage />} />
          <Route path="/org/:role" element={<OrgLoginPage />} />
          <Route path="/org/setup-super-admin" element={<SetupSuperAdminPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['customer', 'farmer', 'dealer', 'delivery', 'supervisor', 'manager', 'ceo', 'admin', 'super_admin']}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute allowedRoles={['customer', 'farmer', 'delivery', 'manager', 'ceo', 'admin', 'super_admin']}>
                <OrderTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard/ceo" element={<ProtectedRoute allowedRoles={['ceo', 'super_admin']}><CEODashboard /></ProtectedRoute>} />
          <Route path="/dashboard/admin" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><CEODashboard /></ProtectedRoute>} />
          <Route path="/dashboard/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><CEODashboard /></ProtectedRoute>} />
          <Route path="/dashboard/manager" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'super_admin']}><ManagerDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/staff/:staffId" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'admin', 'super_admin']}><StaffPortfolioPage /></ProtectedRoute>} />
          <Route path="/dashboard/manager/add-staff" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'super_admin']}><AddStaffPage /></ProtectedRoute>} />
          <Route path="/dashboard/deals" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'super_admin']}><ManageDealsPage /></ProtectedRoute>} />
          <Route path="/dashboard/dealers/add" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'super_admin']}><AddDealerPage /></ProtectedRoute>} />
          <Route path="/dashboard/dealers/:dealerId/edit" element={<ProtectedRoute allowedRoles={['manager', 'ceo', 'super_admin']}><AddDealerPage /></ProtectedRoute>} />
          <Route path="/dashboard/dealer" element={<ProtectedRoute allowedRoles={['dealer']}><DealerDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/dealer/equipment/add" element={<ProtectedRoute allowedRoles={['dealer']}><AddEquipmentDealPage /></ProtectedRoute>} />
          <Route path="/dashboard/dealer/equipment/:dealId/edit" element={<ProtectedRoute allowedRoles={['dealer']}><AddEquipmentDealPage /></ProtectedRoute>} />
          <Route path="/dashboard/supervisor" element={<ProtectedRoute allowedRoles={['supervisor', 'manager', 'ceo', 'super_admin']}><SupervisorDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/supervisor/add-farmer" element={<ProtectedRoute allowedRoles={['supervisor', 'manager', 'ceo', 'super_admin']}><AddFarmerPage /></ProtectedRoute>} />
          <Route path="/dashboard/products/:farmerId" element={<ProtectedRoute allowedRoles={['supervisor', 'manager', 'ceo', 'super_admin']}><ManageProductsPage /></ProtectedRoute>} />
          <Route path="/dashboard/supervisor/farmer/:farmerId/products" element={<ProtectedRoute allowedRoles={['supervisor', 'manager', 'ceo', 'super_admin']}><ManageProductsPage /></ProtectedRoute>} />
          <Route path="/dashboard/farmer" element={<ProtectedRoute allowedRoles={['farmer']}><FarmerDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/delivery" element={<ProtectedRoute allowedRoles={['delivery']}><DeliveryDashboard /></ProtectedRoute>} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/cancellation-and-refunds" element={<CancellationRefundsPage />} />
        </Routes>
      </Suspense>
      {showFeatureFlash && <FeatureFlashSlider />}
      {!hideMarketingChrome && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
